import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { euros, frDate } from "@/lib/weddingApi";
import { EVENT_TYPES } from "@/lib/eventTypes";
import ReceptionForm, { emptyReception, buildPayload } from "./ReceptionForm";
import { Plus, Search, AlertTriangle, Mail, CalendarDays } from "lucide-react";

const STATUS_LABELS = {
  draft: "Brouillon",
  access_sent: "Accès envoyé",
  in_progress: "En cours",
  validated: "Validé",
  cancelled: "Annulé",
};

export default function Receptions() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [filters, setFilters] = useState({ status: "", event_type: "", date_from: "", date_to: "", q: "" });
  const [page, setPage] = useState(1);
  const [form, setForm] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const params = { page, limit: 25 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/admin/weddings", { params });
      setItems(data.items);
      setMeta({ total: data.total, page: data.page, pages: data.pages });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/weddings-defaults").then(({ data }) => setDefaults(data.contract)).catch(() => {});
  }, []);

  const create = async () => {
    setError("");
    setSubmitting(true);
    try {
      await api.post("/admin/weddings", buildPayload(form));
      setForm(null);
      setPage(1);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const setFilter = (patch) => { setPage(1); setFilters({ ...filters, ...patch }); };

  return (
    <div data-testid="admin-receptions-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline">Dossiers confirmés</p>
          <h1 className="font-display text-4xl mt-3 font-light">Réceptions</h1>
          <p className="mt-2 text-sm text-[#697585]">{meta.total} dossier(s)</p>
        </div>
        <div className="flex gap-3">
          <Link to="/admin/reception-emails" data-testid="receptions-import-link" className="btn-ghost inline-flex items-center gap-2">
            <Mail size={14} /> Emails & imports
          </Link>
          <button data-testid="reception-new-btn" className="btn-primary inline-flex items-center gap-2"
                  onClick={() => setForm(emptyReception(defaults))}>
            <Plus size={14} /> Nouveau dossier
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="relative lg:col-span-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#697585]" />
          <input data-testid="receptions-search" className="field w-full pl-9"
                 placeholder="Titre, organisateur, email, référence" value={filters.q}
                 onChange={(e) => setFilter({ q: e.target.value })} />
        </div>
        <select data-testid="receptions-type-filter" className="field" value={filters.event_type}
                onChange={(e) => setFilter({ event_type: e.target.value })}>
          <option value="">Tous les types</option>
          {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select data-testid="receptions-status-filter" className="field" value={filters.status}
                onChange={(e) => setFilter({ status: e.target.value })}>
          <option value="">Tous les statuts</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <div className="flex items-center gap-2">
          <input data-testid="receptions-date-from" type="date" className="field w-full" value={filters.date_from}
                 onChange={(e) => setFilter({ date_from: e.target.value })} />
          <input data-testid="receptions-date-to" type="date" className="field w-full" value={filters.date_to}
                 onChange={(e) => setFilter({ date_to: e.target.value })} />
        </div>
      </div>

      {error && <p data-testid="receptions-error" className="mt-5 text-sm text-red-600">{error}</p>}

      <ul className="mt-8 space-y-3">
        {items.map((w) => (
          <li key={w.id} data-testid={`reception-row-${w.id}`}>
            <Link to={`/admin/receptions/${w.id}`} className="block bg-white border border-[#D9D3C5] p-5 hover:border-[#B8956A] transition-colors">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="overline">
                    {w.reference} · <span className="text-[#B8956A]">{w.event_type_label}</span>
                    {w.is_demo ? " · Démonstration" : ""}
                  </p>
                  <p className="font-display text-xl mt-1">{w.event_title}</p>
                  <p className="text-sm text-[#697585] mt-1">
                    {frDate(w.event_date)}{w.organizer_name ? ` · ${w.organizer_name}` : ""}
                  </p>
                </div>
                <div className="text-right">
                  <span className={`text-xs uppercase tracking-[0.16em] ${w.status === "validated" ? "text-emerald-700" : "text-[#B8956A]"}`}>
                    {STATUS_LABELS[w.status] || w.status}
                  </span>
                  <p className="text-sm mt-1">{euros(w.budget_total_cents)}</p>
                  <p className="text-xs text-[#697585] mt-1">
                    {w.counts?.adults || 0} adultes · {w.counts?.children || 0} enfants
                  </p>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#697585]">
                <span className="flex items-center gap-1">
                  <CalendarDays size={12} />
                  {w.deadline_date ? `À finaliser avant le ${frDate(w.deadline_date)}` : "Échéance à définir"}
                </span>
                {!!w.incomplete_guests && <span className="text-[#B8956A]">{w.incomplete_guests} invités incomplets</span>}
                {!!w.allergy_guests && <span>{w.allergy_guests} allergies déclarées</span>}
                {!!w.pending_exceptions && <span className="text-[#B8956A]">{w.pending_exceptions} exception(s) à traiter</span>}
                {!!w.pending_change_requests && <span className="text-[#B8956A]">{w.pending_change_requests} demande(s) en attente</span>}
                {w.late && <span className="flex items-center gap-1 text-red-600"><AlertTriangle size={12} /> Échéance dépassée</span>}
                {w.invitation?.state && !["idle", "sent"].includes(w.invitation.state) && (
                  <span className="text-red-600">Invitation : {w.invitation.state}</span>
                )}
              </div>
            </Link>
          </li>
        ))}
      </ul>

      {!items.length && <p data-testid="receptions-empty" className="mt-10 text-sm text-[#697585]">Aucun dossier pour ces critères.</p>}

      {meta.pages > 1 && (
        <div className="mt-8 flex items-center gap-3">
          <button data-testid="receptions-prev" className="btn-ghost" disabled={meta.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}>Précédent</button>
          <span className="text-sm text-[#697585]">Page {meta.page} / {meta.pages}</span>
          <button data-testid="receptions-next" className="btn-ghost" disabled={meta.page >= meta.pages}
                  onClick={() => setPage((p) => p + 1)}>Suivant</button>
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-auto" onClick={() => setForm(null)}>
          <div onClick={(e) => e.stopPropagation()} className="my-6">
            <ReceptionForm
              form={form}
              setForm={setForm}
              onSubmit={create}
              onCancel={() => setForm(null)}
              submitting={submitting}
              error={error}
            />
          </div>
        </div>
      )}
    </div>
  );
}
